import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Checkbox } from '@/shared/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'@/shared/components/ui/select';
import {
  Truck,
  Search,
  Plus,
  FileDown,
  Eye,
  XCircle,
  Mail,
  FileCode,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Loader2 } from
'lucide-react';

import { oigAPI } from '@/shared/services/api';

export const CTeList = () => {const { t } = useTranslation("oig");
  const [selectedUnit, setSelectedUnit] = useState('02.892.407/0002-87');
  const [ambiente, setAmbiente] = useState('producao');
  const [cteList, setCteList] = useState([]);
  const [cteConfig, setCteConfig] = useState({ companies: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [listData, configData] = await Promise.all([
        oigAPI.getCTeList(),
        oigAPI.getConfig()]
        );

        setCteList(listData || []);
        setCteConfig(configData || { companies: [] });
      } catch (err) {
        console.error('Failed to fetch CT-e data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const statusColors = {
    'AUTORIZADA': 'bg-emerald-500 text-white',
    'PENDENTE': 'bg-amber-500 text-white',
    'CANCELADA': 'bg-gray-500 text-white',
    'ERRO': 'bg-red-500 text-white'
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="oig-cte-list">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk'] flex items-center gap-3">
            <Truck className="h-8 w-8 text-blue-600" />{t("listagemDeCte", "Listagem de CT-e")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("conhecimentoDeTransporte", "Conhecimento de Transporte Eletr\xF4nico")}

          </p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700 gap-2">
          <Plus className="h-4 w-4" />{t("novoCte", "Novo CT-e")}

        </Button>
      </div>

      {/* Alert Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
        <AlertTriangle className="h-5 w-5 text-blue-600" />
        <p className="text-sm text-blue-800">
          <span className="font-medium">{t("ateno", "Aten\xE7\xE3o!")}</span>{t("oSistemaEmissor", "O sistema emissor de CT-e est\xE1 no CNPJ:")}
          <span className="font-mono font-bold ml-1">{selectedUnit}</span>{t("eNoAmbiente", "e no ambiente:")}
          <span className="font-bold ml-1 uppercase">{ambiente === 'producao' ? 'PRODUÇÃO' : 'HOMOLOGAÇÃO'}</span>
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">{t("unidade", "Unidade")}</label>
          <Select value={selectedUnit} onValueChange={setSelectedUnit}>
            <SelectTrigger className="w-[280px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {cteConfig.companies && cteConfig.companies.length > 0 ?
              cteConfig.companies.map((c) =>
              <SelectItem key={c.id} value={c.cnpj}>
                    {c.cnpj} - {c.identificacao}
                  </SelectItem>
              ) :

              <SelectItem value={selectedUnit}>
                  {selectedUnit}
                </SelectItem>
              }
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">{t("ambiente", "Ambiente")}</label>
          <div className="flex gap-1">
            <Button
              variant={ambiente === 'homologacao' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setAmbiente('homologacao')}
              className={ambiente === 'homologacao' ? 'bg-gray-600' : ''}>{t("homologao", "HOMOLOGA\xC7\xC3O")}


            </Button>
            <Button
              variant={ambiente === 'producao' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setAmbiente('producao')}
              className={ambiente === 'producao' ? 'bg-blue-600' : ''}>{t("produo", "PRODU\xC7\xC3O")}


            </Button>
          </div>
        </div>
      </div>

      {/* Search */}
      <Card className="card-elevated">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input placeholder="Buscar CT-e..." className="w-64" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="gap-1">
                <Mail className="h-4 w-4" />{t("email", "E-mail")}

              </Button>
              <Button variant="outline" size="sm" className="gap-1 text-red-600">
                <FileDown className="h-4 w-4" />{t("pdf", "PDF")}

              </Button>
              <Button variant="outline" size="sm" className="gap-1 text-blue-600">
                <FileCode className="h-4 w-4" />{t("xml", "XML")}

              </Button>
              <Button variant="outline" size="sm" className="gap-1">
                <Eye className="h-4 w-4" />{t("detalhes", "Detalhes")}

              </Button>
              <Button variant="outline" size="sm" className="gap-1 text-red-600">
                <XCircle className="h-4 w-4" />{t("cancelar", "Cancelar")}

              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="card-elevated">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-blue-700 text-white">
                <tr>
                  <th className="py-3 px-4 text-left">
                    <Checkbox />
                  </th>
                  <th className="py-3 px-4 text-left font-medium">{t("situao", "Situa\xE7\xE3o")}</th>
                  <th className="py-3 px-4 text-left font-medium">{t("srie", "S\xE9rie")}</th>
                  <th className="py-3 px-4 text-left font-medium">{t("nCte", "N\xBA CT-e")}</th>
                  <th className="py-3 px-4 text-left font-medium">{t("dataEmisso", "Data Emiss\xE3o")}</th>
                  <th className="py-3 px-4 text-left font-medium">{t("remetente", "Remetente")}</th>
                  <th className="py-3 px-4 text-left font-medium">{t("destinatrio", "Destinat\xE1rio")}</th>
                  <th className="py-3 px-4 text-left font-medium">{t("cfop", "CFOP")}</th>
                  <th className="py-3 px-4 text-left font-medium">{t("modal", "Modal")}</th>
                  <th className="py-3 px-4 text-right font-medium">{t("valorFrete", "Valor Frete")}</th>
                  <th className="py-3 px-4 text-right font-medium">{t("valorCarga", "Valor Carga")}</th>
                </tr>
              </thead>
              <tbody>
                {cteList.map((cte, index) =>
                <tr key={cte.id} className={`border-b border-border hover:bg-muted/30 ${index % 2 === 0 ? 'bg-white dark:bg-transparent' : 'bg-gray-50 dark:bg-muted/10'}`}>
                    <td className="py-3 px-4">
                      <Checkbox />
                    </td>
                    <td className="py-3 px-4">
                      <Badge className={statusColors[cte.situacao]}>
                        {cte.situacao}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-sm">{cte.serie}</td>
                    <td className="py-3 px-4 font-mono text-sm font-medium">{cte.numeroCTe}</td>
                    <td className="py-3 px-4 text-sm">
                      {cte.dataEmissao && new Date(cte.dataEmissao).toLocaleString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <p className="text-sm font-medium">{cte.remetente}</p>
                        <p className="text-xs text-muted-foreground font-mono">{cte.cnpjRemetente}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <p className="text-sm font-medium">{cte.destinatario}</p>
                        <p className="text-xs text-muted-foreground font-mono">{cte.cnpjDestinatario}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4 font-mono text-sm">{cte.cfop}</td>
                    <td className="py-3 px-4">
                      <Badge variant="outline">{cte.modal}</Badge>
                    </td>
                    <td className="py-3 px-4 text-right font-medium text-blue-600">
                      {formatCurrency(cte.valorFrete)}
                    </td>
                    <td className="py-3 px-4 text-right font-medium">
                      {formatCurrency(cte.valorCarga)}
                    </td>
                  </tr>
                )}
                {cteList.length === 0 &&
                <tr>
                    <td colSpan={11} className="py-8 text-center text-muted-foreground">{t("nenhumCteEncontrado", "Nenhum CT-e encontrado")}

                  </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t border-border">
            <p className="text-sm text-muted-foreground">{t("mostrando1", "Mostrando 1 -")}
              {cteList.length}{t("de", "de")}{cteList.length}{t("registros", "registros")}
            </p>
            <div className="flex items-center gap-2">
              <Select defaultValue="100">
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">{t("25", "25")}</SelectItem>
                  <SelectItem value="50">{t("50", "50")}</SelectItem>
                  <SelectItem value="100">{t("100", "100")}</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex gap-1">
                <Button variant="outline" size="icon" disabled>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="default" size="sm" className="bg-blue-600">1</Button>
                <Button variant="outline" size="icon" disabled>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>);

};